import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { t, tagesdatum } from './sprache'

/**
 * Was der Ausdruck braucht und der Bildschirm nicht — an einer Stelle
 * (7.2 Abschnitt 19, R810, R811).
 *
 * **Zweierlei steht hier, und beides kann CSS nicht:**
 *
 * 1. **Aufklappen vor dem Druck.** Ein zugeklapptes Kapitel wäre auf Papier
 *    eine Überschrift ohne Inhalt, und der Leser hielte es für leer. Ein
 *    `<details>` läßt sich durch kein Stilblatt öffnen — also öffnet es der
 *    Browser selbst, kurz bevor er druckt, und schließt danach wieder, was
 *    vorher zu war.
 * 2. **Der Seitenfuß.** Anlage, Datum und — wo es einen gibt — der Prüfcode
 *    dessen, was gerade zu sehen ist. Ein herausgetrenntes Blatt muß
 *    zuordenbar bleiben, und im Umlauf eines Konzernabschlusses werden
 *    Blätter herausgetrennt (2.12 Abschnitt 3.6).
 *
 * **Warum zentral und nicht im Bericht.** Der Befund vom 2026-09-26 war, daß
 * es im ganzen Baum keine einzige Druckregel gab. Sie jetzt an der
 * Prüferansicht zu bauen hieße, sie an einem von fünfzehn Bildschirmen zu
 * bauen — und in vierzehn Tagen kommt dieselbe Meldung von der Bilanz.
 */

/** Setzt den Prüfcode, der im Seitenfuß stehen soll — `null` heißt: keiner. */
const Vermerkkontext = createContext<(vermerk: string | null) => void>(() => undefined)

/**
 * Der Rahmen um die ganze Anwendung: Seitenfuß und Druckvorbereitung.
 */
export function Druckrahmen({ children }: { children: React.ReactNode }) {
  const [vermerk, setzeVermerk] = useState<string | null>(null)

  /*
    **Jedes aufklappbare Kapitel öffnet vor dem Druck** (R810).

    Gemerkt wird, was vorher ZU war, und genau das wird danach wieder
    zugeklappt. Alles hinterher zu öffnen wäre bequemer und falsch: Wer ein
    Kapitel zugeklappt hat, hat das gewollt, und ein Ausdruck ist kein Grund,
    ihm den Bildschirm umzuräumen.

    `afterprint` kommt auch, wenn der Anwender den Druckdialog abbricht —
    dieselbe Wiederherstellung, derselbe Weg.
  */
  useEffect(() => {
    let geschlossen: HTMLDetailsElement[] = []

    function vorDemDruck() {
      geschlossen = [...document.querySelectorAll<HTMLDetailsElement>('details:not([open])')]
      for (const kapitel of geschlossen) {
        kapitel.open = true
      }
    }

    function nachDemDruck() {
      for (const kapitel of geschlossen) {
        kapitel.open = false
      }

      geschlossen = []
    }

    window.addEventListener('beforeprint', vorDemDruck)
    window.addEventListener('afterprint', nachDemDruck)

    return () => {
      window.removeEventListener('beforeprint', vorDemDruck)
      window.removeEventListener('afterprint', nachDemDruck)
    }
  }, [])

  return (
    <Vermerkkontext.Provider value={setzeVermerk}>
      {children}

      {/*
        **Der Fuß steht immer im Baum** und ist am Bildschirm ausgeblendet.
        Ihn erst beim Drucken einzuhängen hieße, ihn in dem Augenblick
        einzuhängen, in dem der Browser das Bild schon aufgeteilt hat.
      */}
      <div className="druckfuss" aria-hidden="true">
        <span>{t('druck.anlage', { anlage: anlagenname() })}</span>
        <span>{tagesdatum(new Date().toISOString())}</span>
        {vermerk && <span className="hash">{t('druck.pruefcode', { code: vermerk })}</span>}
      </div>
    </Vermerkkontext.Provider>
  )
}

/**
 * Woran ein Prüfer die Anlage erkennt.
 *
 * **Der Rechnername, nicht ein Hauseinstellungstext.** Zwei Anlagen desselben
 * Kunden — Abnahme und Wirkbetrieb — tragen denselben Konzernnamen und sind
 * nicht dasselbe. Was sie unterscheidet, steht in der Adresszeile.
 */
function anlagenname(): string {
  return typeof window === 'undefined' ? '' : window.location.host
}

/**
 * Meldet den Prüfcode an, der im Seitenfuß stehen soll.
 *
 * **Ein Bericht ohne Prüfcode meldet nichts an** und läßt die Stelle leer,
 * statt etwas zu erfinden (R811).
 */
export function useDruckvermerk(vermerk: string | null) {
  const setzen = useContext(Vermerkkontext)

  useEffect(() => {
    setzen(vermerk)
    return () => setzen(null)
  }, [vermerk, setzen])
}

/**
 * Der Knopf, der den Druck des Browsers auslöst.
 *
 * **Er ist eine Bequemlichkeit, kein zweiter Weg.** Strg+P tut dasselbe; der
 * Knopf steht da, weil ein Prüfer am Bericht sehen soll, daß er zum Drucken
 * gedacht ist. Im Ausdruck selbst ist er weg — wie jeder Knopf (R810).
 */
export function Druckknopf() {
  const drucke = useCallback(() => window.print(), [])

  return (
    <button type="button" onClick={drucke}>
      {t('druck.knopf')}
    </button>
  )
}
